import { groupMaterials } from "./PlanningGrid/PlanningGrid.utils";
import { mapVersionAttachments } from "./PlanningGrid/MaterialSection/MaterialSection.utils";
import { createSelectableAddendumItem } from "./PlanningGrid/AddendumSection/addendumSection.utils";

/**
 * Normalizes a selected file/attachment entry for use with the release flow.
 *
 * Materials (`mapVersionAttachments`) and addenda (`createSelectableAddendumItem`)
 * already spread the real attachment fields (id, releaseManagement, ...) at the
 * top level. Chapter entries (raw `capitulo.attachments` / `normalizeChapterAttachments`)
 * do NOT: they keep the chapter-attachment join record's own `id`, while the
 * real attachment (with `releaseManagement`, real id) is nested under
 * `.attachment`. This unwraps that nesting so the release flow always
 * receives the real attachment, regardless of whether it's already been
 * through `normalizeChapterAttachments` (which sets `parentId`/`blockId` on
 * the wrapper) or is still the raw wrapper (in which case `overrides` should
 * supply `parentId`/`blockId` explicitly).
 *
 * @param {Object} item - A chapter-attachment wrapper or already-normalized entry.
 * @param {Object} [overrides] - `{ parentId, blockId }` to use when the wrapper itself doesn't carry them yet.
 * @returns {Object} - The real attachment, normalized for the release flow.
 */
export const toReleaseAttachment = (item, overrides = {}) => {
  if (item?.attachment) {
    return {
      ...item.attachment,
      parentId: overrides.parentId ?? item.parentId,
      blockId: overrides.blockId ?? item.blockId,
      blockNumber: overrides.blockNumber ?? item.blockNumber,
      chapterNumber: overrides.chapterNumber ?? item.chapterNumber,
      versionNumber:
        overrides.versionNumber ?? item.versionNumber ?? item.version,
      type: "chapter",
    };
  }

  return item;
};

/**
 * Builds "same file, different version" sibling groups from the Planning
 * (blocks/materials) response, mirroring the shape `findReleasedSiblingVersions`
 * (src/context/releaseFlow.utils.js) expects. Needed because that helper's
 * usual grouping (`groupAttachmentsForRelease`) is built for TableListFiles'
 * flat attachment list + attachmentContext.compileAll/compiledChapter/compileMaterial,
 * which does not match this view's nested Planning data shape.
 *
 * @param {Object} planningData - statePlanning.data (`{ blocks, materials }`)
 * @returns {Array<Array<Object>>}
 */
export function buildPlanningReleaseGroups(planningData) {
  const { materials = [], blocks = [] } = planningData || {};

  const materialGroups = groupMaterials(materials).map((group) => {
    const typeId = Object.keys(group).find((key) => key !== "name");
    const items = group[typeId] || [];

    const versionAttachments = items.flatMap(mapVersionAttachments);
    const addenda = items
      .flatMap((version) => version.addenda || [])
      // Materials belong to short-form/variety content, which resolves
      // addendum associations via the episode lookup (see addendum-associations.md).
      .map((addendum) => createSelectableAddendumItem(addendum, false));

    return [...versionAttachments, ...addenda];
  });

  const chapterGroups = blocks.flatMap((block) =>
    (block.chapters || []).map((chapter) => {
      const attachments = (chapter.attachments || []).map((attachment) =>
        toReleaseAttachment(attachment, {
          parentId: chapter.id,
          blockId: chapter.blockId,
          blockNumber: block.number,
          chapterNumber: chapter.number,
        })
      );
      const addenda = (chapter.addenda || []).map((addendum) =>
        // Chapters belong to long-form content, which resolves addendum
        // associations via the chapter lookup.
        createSelectableAddendumItem(addendum, true)
      );

      return [...attachments, ...addenda];
    })
  );

  return [...materialGroups, ...chapterGroups];
}
